import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import AuthMethodsOffer from "@/components/AuthMethodsOffer";
import { CookieBanner } from "@/components/CookieBanner";
import { CustomerPreviewBanner } from "@/components/CustomerPreviewBanner";
import { EditModeBootstrap } from "@/components/edit/EditModeBootstrap";
import { EditModeFAB } from "@/components/edit/EditModeFAB";
import { useShellI18nOverrides } from "@/components/edit/useShellI18nOverrides";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { StorefrontTrialBanner } from "@/components/StorefrontTrialBanner";
import { TenantBrandTheme } from "@/components/TenantBrandTheme";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useCustomLanguageBundles } from "@/i18n/useCustomLanguageBundles";
import { useRegisterAvailableLanguages } from "@/i18n/useRegisterAvailableLanguages";
import { CurrencyProvider } from "@/lib/currency";
import { usePersonalCodeCapture } from "@/lib/personalCode";
import { useSeo } from "@/lib/seo";
import { resolveShell } from "@/shell/resolveShell";
import { useCustomerPageTitle } from "@/shell/useCustomerPageTitle";

/**
 * Root language guard.
 *
 * Every route is mounted under `/:lang/*`. This component:
 *   1. Validates `:lang` against the supported set (redirects to default if not).
 *   2. Pushes the negotiated language into i18next + `<html lang>`.
 *
 * Translation strings themselves live in `src/i18n/locales/...`.
 */
export default function App(): JSX.Element {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  const active = isSupportedLang(lang) ? lang : null;

  // KAN-1574 — `?p=` from an outreach link; a shop also reports the visit.
  usePersonalCodeCapture({ reportVisit: resolveShell() === "tenant" });

  useEffect(() => {
    if (active && i18n.language !== active) {
      void i18n.changeLanguage(active);
      // Server-resolved display names (process labels, material names,
      // category labels) now ride the request's Accept-Language, so every
      // cached response belongs to the language it was fetched under.
      // Refetch instead of adding `lang` to ~90 query keys by hand.
      void queryClient.invalidateQueries();
    }
    if (active && typeof document !== "undefined") {
      document.documentElement.lang = active;
    }
  }, [active, i18n, queryClient]);

  if (!active) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }

  return <ShellFrame />;
}

function ShellFrame(): JSX.Element {
  // Hide the customer chrome (header + footer + cookie banner) under
  // /admin/*, /shopfloor/* and legacy /onboarding/* entry routes. Admin
  // has its own layout shell with a
  // sidebar; Dílna (shopfloor terminál) má vlastní minimální chrome —
  // storefront hlavička na tabletu u pásu nemá co dělat.
  const { pathname } = useLocation();
  const isAdmin = /\/[a-z]{2}\/(admin|shopfloor|onboarding)(\b|\/)/.test(
    pathname,
  );

  // Merge shell-scoped i18n overrides as soon as branding resolves.
  // Hook itself no-ops on the platform-admin shell.
  useShellI18nOverrides();
  // Register the platform's enabled languages (from the same branding
  // payload) into the runtime registry so the switcher + gate honour
  // admin-added locales.
  useRegisterAvailableLanguages();
  // Merge custom-locale FE bundles so an admin-added language renders
  // app-wide (falls back to the default language for untranslated keys).
  useCustomLanguageBundles();

  if (isAdmin) {
    // Admin shell owns its own ``<title>`` via ``AdminLayout`` +
    // ``useAdminPageTitle``. We deliberately don't call ``useSeo``
    // here because AdminLayout sits below us in the tree — a
    // layout-priority call from this component would commit AFTER
    // AdminLayout's (parents fire last in useLayoutEffect) and
    // clobber the per-route admin title.
    // The offer rides along here too. Mounting it only on the customer
    // shell would mean an org admin — the account most worth having two
    // ways into — never saw it, because the admin branch returns before
    // the chrome below is ever rendered.
    // ⚠ The wrapper is load-bearing, not cosmetic. ``AuthMethodsOffer``
    // is a SIBLING of the shell below it, and that shell used to be
    // ``min-h-screen`` — so whenever the offer showed, the document was
    // ``strip + 100vh`` and EVERY admin page grew a scrollbar exactly as
    // tall as the strip (measured: 161 px). Owning the viewport height
    // here and letting the shell grow into what is left keeps the two
    // from stacking. Renders identically when the offer is hidden, which
    // is the usual case.
    return (
      <div className="flex min-h-screen flex-col">
        <AuthMethodsOffer />
        <Outlet />
      </div>
    );
  }

  return (
    <TenantBrandTheme>
      <CurrencyProvider>
        <CustomerSeoDefault />
        <div className="flex min-h-screen flex-col">
          {/* Nad hlavičkou, ne pod ní: říká, čí očima je celá stránka
              vidět — včetně té hlavičky (KAN-747). */}
          <CustomerPreviewBanner />
          {/* Zkušební nákup se pozná po celé cestě, ne až na pokladně
              (KAN-761): objednávka z pozvánky nevstoupí do tržeb a
              tenant to musí vědět dřív, než ji zaplatí. Obojí se může
              potkat: náhled zákazníka otevřený z pozvánky nese oba. */}
          <StorefrontTrialBanner />
          <Header />
          {/* Above the page, below the header: it is about the account,
              not about whatever page happens to be open. Renders
              nothing at all unless the server says it is due. */}
          <AuthMethodsOffer />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
          <CookieBanner />
          <EditModeBootstrap />
          <EditModeFAB />
        </div>
      </CurrencyProvider>
    </TenantBrandTheme>
  );
}

/**
 * Shell-level fallback title for tenant / platform-marketing routes.
 * Sits as a sibling of ``<Outlet />`` so its ``useLayoutEffect`` fires
 * BEFORE any child page's ``useEffect`` — page-level ``useSeo`` calls
 * therefore always override this default.
 */
function CustomerSeoDefault(): null {
  const { pathname } = useLocation();
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const pathAfterLang = pathname.replace(new RegExp(`^/${lang}/?`), "/");
  const title = useCustomerPageTitle();
  useSeo({ title, path: pathAfterLang, lang, priority: "layout" });
  return null;
}
